export type RejectReasonCode = 'too_expensive' | 'skills_gap' | 'other'
export type ApplicationStatus =
  | 'saved'
  | 'applied'
  | 'in_process'
  | 'offer'
  | 'rejected'
  | 'hired'
  | 'archived'
  | 'ghosted'
  | 'withdrawn'
  | 'offer_declined'
  | 'accepted'

export type SalaryPeriod = 'hourly' | 'monthly' | 'yearly' | null

export type WorkModeType = 'onsite' | 'remote' | 'hybrid' | null

export type SalaryRange = {
  min: number
  max: number
  currency: string
  period: SalaryPeriod
} | null

export type Stage = {
  id: string
  name: string
  completed: boolean
}

export type RejectReason = {
  code: RejectReasonCode
  details?: string
}

export type Company = {
  id: string
  name: string
  nameNormalized: string
  info: string | null
  createdAt: string
  updatedAt: string
}

export type Application = {
  id: string
  company: string
  companyId?: string | null
  companyInfo?: string | null
  positionTitle?: string | null
  sourceUrl: string
  // DEPRECATED - kept for backward compatibility
  expectedSalary: number | null
  expectedHourly: number | null
  // NEW target salary fields
  targetAmount: number | null
  targetPeriod: SalaryPeriod
  targetCurrency: Currency
  salaryRange: SalaryRange
  techStack: string[]
  contractType?: string | null
  benefits?: string | null
  workMode?: WorkModeType
  officeDays?: number | null
  processStages: Stage[]
  status: ApplicationStatus
  archived?: boolean
  favorite?: boolean
  note?: string | null
  followUpAt?: string | null
  rejectReason?: RejectReason
  offerDecisionReason?: string | null
  rawMarkdown?: string | null
  isDraft?: boolean
  createdAt: string
  updatedAt: string
}

export type ExtractedPosting = {
  company: string
  companyId?: string | null
  companyInfo?: string | null
  positionTitle?: string | null
  salaryRange: SalaryRange
  salaryPeriod?: SalaryPeriod
  techStack: string[]
  stages: string[]
  contractType?: string | null
  benefits?: string | null
  workMode?: WorkModeType
  officeDays?: number | null
  rawMarkdown?: string | null
  draftId?: string | null
  isDraft?: boolean
}

export type SankeyNode = { name: string; color?: string; fill?: string }
export type SankeyLink = { source: number; target: number; value: number }

export type SankeyDatum = {
  nodes: SankeyNode[]
  links: SankeyLink[]
}

export type DepthStats = {
  distribution: { depth: number; count: number }[]
  avgDepth: number
  maxDepth: number
  maxDepthCompany: string
  total: number
  // Completion stats
  completionDistribution: { bucket: string; count: number }[]
  avgCompletion: number
  fullyCompleted: number
  appsWithStages: number
}

export type Currency = 
  | 'USD'  // US Dollar
  | 'EUR'  // Euro
  | 'GBP'  // British Pound
  | 'PLN'  // Polish Zloty
  | 'CHF'  // Swiss Franc
  | 'CZK'  // Czech Koruna
  | 'SEK'  // Swedish Krona
  | 'NOK'  // Norwegian Krone
  | 'DKK'  // Danish Krone
  | 'CAD'  // Canadian Dollar
  | 'AUD'  // Australian Dollar
  | 'original'  // Use original salary as posted

// Display format for salary rates
export type SalaryDisplayFormat = 'hourly' | 'monthly' | 'yearly' | 'original'

// Display mode for application list
export type DisplayMode = 'cards' | 'table'

// Date display format
export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'

// Theme mode
export type ThemeMode = 'light' | 'dark' | 'system'

export type OnboardingGoal =
  | 'track_applications'
  | 'improve_resume'
  | 'prepare_interviews'
  | 'compare_offers'
  | 'exploring'

export type CurrentSearchStage =
  | 'starting'
  | 'actively_applying'
  | 'interviewing'
  | 'considering_offers'
  | 'paused'

export type OnboardingResponses = {
  version: number
  completedAt: string | null
  goal: {
    value: OnboardingGoal | null
    label: string | null
  }
  currentSearchStage: {
    value: CurrentSearchStage | null
    label: string | null
  }
  preferences: {
    autoGhost: {
      enabled: boolean
      label: string
    }
    autoArchive: {
      enabled: boolean
      label: string
    }
    dateFormat: {
      value: DateFormat
      label: string
      example: string
    }
  }
}

// Table columns that can be toggled
export type TableColumn =
  | 'favorite'
  | 'company'
  | 'status'
  | 'salary'
  | 'target'
  | 'stages'
  | 'note'
  | 'createdAt'
  | 'updatedAt'

export type UserSettings = {
  autoConvertRates: boolean
  emailIntegrationEnabled: boolean
  autoGhostEnabled: boolean
  autoArchiveEnabled: boolean
  // Currency conversion settings
  currencyConversionEnabled: boolean
  targetCurrency: Currency
  // Salary display format
  salaryDisplayFormat: SalaryDisplayFormat
  // User profile
  displayName: string | null
  // UI preferences
  defaultDisplayMode: DisplayMode
  dateFormat: DateFormat
  // Table column visibility
  visibleColumns: TableColumn[]
  // Resume analysis consent
  resumeConsentGiven: boolean
  resumeConsentDate: string | null
  // Theme mode
  themeMode: ThemeMode
  // First-run onboarding
  onboardingCompleted: boolean
  onboardingGoal: OnboardingGoal | null
  currentSearchStage: CurrentSearchStage | null
  onboardingResponses: OnboardingResponses | null
  productTourCompleted: boolean
}

// Subscription & Billing
export type SubscriptionPlan = 'free' | 'pro'
export type SubscriptionStatus = 'active' | 'cancelled' | 'past_due' | 'trialing' | 'incomplete'

export type Subscription = {
  id: string
  userId: string
  plan: SubscriptionPlan
  status: SubscriptionStatus
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  createdAt: string
  updatedAt: string
}

// Resume Analysis
export type ResumeAnalysis = {
  id: string
  user_id: string
  file_path: string | null
  file_name: string
  overall_score: number
  strengths: string[]
  weaknesses: string[]
  suggestions: string[]
  ats_score: number
  missing_keywords: string[]
  formatting_issues: string[]
  created_at: string
  updated_at: string
}

// Resume Comparison (CV vs Job) - API response
export type ResumeComparison = {
  id?: string // Present when saved to DB
  match_score: number
  matched_skills: string[]
  missing_skills: string[]
  experience_match: {
    score: number
    feedback: string
  }
  strengths_for_role: string[]
  gaps_for_role: string[]
  suggestions: string[]
  interview_tips: string[]
  overall_assessment: string
  application_id: string
  company: string
  position: string | null
  resume_file: string
  analyzed_at: string
}

// Resume Comparison stored in DB
export type ResumeComparisonRecord = {
  id: string
  user_id: string
  application_id: string
  resume_analysis_id: string | null
  match_score: number
  matched_skills: string[]
  missing_skills: string[]
  experience_match_score: number | null
  experience_match_feedback: string | null
  strengths_for_role: string[]
  gaps_for_role: string[]
  suggestions: string[]
  interview_tips: string[]
  overall_assessment: string | null
  company: string
  position_title: string | null
  resume_file_name: string | null
  created_at: string
  updated_at: string
}

// Quiz
export type QuizDifficulty = 'easy' | 'medium' | 'hard'
export type QuizCategory = 'technical' | 'conceptual' | 'behavioral'

export type QuizQuestion = {
  id: string
  category: QuizCategory
  text: string
  options: string[]
  correct_option: number      // index 0-3
  user_answer: number | null  // index 0-3 or null
  is_correct: boolean | null
  explanation: string
}

export type QuizSession = {
  id: string
  user_id: string
  application_id: string | null
  difficulty: QuizDifficulty
  categories: QuizCategory[]
  total_questions: number
  correct_answers: number
  score: number
  questions: QuizQuestion[]
  created_at: string
}

// Mock Interview
export type MockInterviewQuestion = {
  id: string
  category: QuizCategory
  text: string
  follow_up_hint: string
  user_answer: string | null
  feedback: string | null
  score: number | null
  strengths: string[]
  improvements: string[]
  sample_answer_hint?: string
}

export type MockInterviewSession = {
  id: string
  user_id: string
  application_id: string | null
  general_role: string | null
  general_tech: string[] | null
  difficulty: QuizDifficulty
  categories: QuizCategory[]
  total_questions: number
  score: number
  questions: MockInterviewQuestion[]
  duration_seconds: number | null
  created_at: string
}

// Pro features that are gated
export type ProFeature =
  | 'advanced_analytics'
  | 'multi_currency'
  | 'unlimited_apps'
  | 'salary_insights'
  | 'resume_analysis'
  | 'resume_match'
  | 'resume_builder'
  | 'practice_quizzes'
  | 'ai_chat'
  | 'mock_interview'

// =========================
// Resume Builder
// =========================
export type ResumeTemplate = 'modern' | 'classic' | 'compact' | 'executive' | 'minimal'

export type ResumeSectionKey =
  | 'summary'
  | 'experience'
  | 'projects'
  | 'skills'
  | 'education'
  | 'certifications'
  | 'languages'
  | 'customSections'

export type ResumeLocale = 'en' | 'es' | 'fr' | 'de' | 'pt' | 'it' | 'pl'

export type ResumeFont = 'sans' | 'serif' | 'mono' | 'lato' | 'ptserif'

export type ResumeContact = {
  fullName?: string
  headline?: string
  email?: string
  phone?: string
  location?: string
  website?: string
  linkedin?: string
  github?: string
  // Visual & layout customization (stored inside the contact JSONB column so
  // we don't need a separate migration; orthogonal to contact information).
  accentColor?: string
  locale?: ResumeLocale
  font?: ResumeFont
  sectionOrder?: ResumeSectionKey[]
}

export type ResumeExperience = {
  id: string
  company: string
  role: string
  location?: string
  startDate?: string // free-form e.g. "Jan 2023"
  endDate?: string   // free-form e.g. "Present"
  bullets: string[]
}

export type ResumeEducation = {
  id: string
  institution: string
  degree?: string
  field?: string
  location?: string
  startDate?: string
  endDate?: string
  details?: string
}

export type ResumeSkillGroup = {
  id: string
  category: string // e.g. "Languages", "Frameworks"
  items: string[]
}

export type ResumeProject = {
  id: string
  name: string
  url?: string
  description?: string
  bullets: string[]
}

export type ResumeCertification = {
  id: string
  name: string
  issuer?: string
  date?: string
  url?: string
}

export type ResumeLanguage = {
  id: string
  name: string
  proficiency?: string // e.g. "Native", "C1", "Conversational"
}

export type ResumeCustomSection = {
  id: string
  title: string
  body: string // markdown-ish plain text; rendered as paragraphs
}

export type Resume = {
  id: string
  userId: string
  title: string
  template: ResumeTemplate
  isMaster: boolean
  tailoredForApplicationId: string | null
  contact: ResumeContact
  summary: string | null
  experience: ResumeExperience[]
  education: ResumeEducation[]
  skills: ResumeSkillGroup[]
  projects: ResumeProject[]
  certifications: ResumeCertification[]
  languages: ResumeLanguage[]
  customSections: ResumeCustomSection[]
  createdAt: string
  updatedAt: string
}

// Raw DB row (snake_case) — used by the repo layer.
export type ResumeRow = {
  id: string
  user_id: string
  title: string
  template: ResumeTemplate
  is_master: boolean
  tailored_for_application_id: string | null
  contact: ResumeContact | null
  summary: string | null
  experience: ResumeExperience[] | null
  education: ResumeEducation[] | null
  skills: ResumeSkillGroup[] | null
  projects: ResumeProject[] | null
  certifications: ResumeCertification[] | null
  languages: ResumeLanguage[] | null
  custom_sections: ResumeCustomSection[] | null
  created_at: string
  updated_at: string
}

// Free tier limits
export const FREE_TIER_LIMITS = {
  maxActiveApps: 10,
  maxResumeAnalysesPerMonth: 1,
  maxQuizzes: 1,
  maxMockInterviews: 1,
} as const

// Pro tier limits
export const PRO_TIER_LIMITS = {
  maxMockInterviewsPerWeek: 2,
} as const

// Follow-up types
export type FollowUpType = 'reminder' | 'interview' | 'email' | 'deadline'

export type FollowUp = {
  id: string
  userId: string
  applicationId: string
  dueAt: string
  title: string
  type: FollowUpType
  note?: string | null
  completed: boolean
  createdAt: string
  updatedAt: string
  // Joined from application
  application?: {
    company: string
    positionTitle: string | null
  }
}
